import { useEffect, useRef } from "react";
import { fileUrl } from "../lib/api";

/** Build overlay style based on admin config. */
const overlayStyle = (style, opacityPct) => {
    const op = Math.max(0, Math.min(100, opacityPct ?? 55)) / 100;
    switch (style) {
        case "none":
            return { background: "transparent" };
        case "light":
            return { background: `linear-gradient(180deg, rgba(255,255,255,${op * 0.3}) 0%, rgba(255,255,255,${op}) 100%)` };
        case "primary":
            return { background: `linear-gradient(180deg, hsl(var(--primary) / ${op * 0.3}) 0%, hsl(var(--primary) / ${op}) 100%)` };
        case "accent":
            return { background: `linear-gradient(180deg, hsl(var(--secondary) / ${op * 0.3}) 0%, hsl(var(--secondary) / ${op}) 100%)` };
        case "dark":
        default:
            return { background: `linear-gradient(180deg, rgba(15,30,45,${op * 0.25}) 0%, rgba(15,30,45,${op}) 100%)` };
    }
};

/**
 * Hero image with admin-editable animation style + overlay.
 * animation: "ken-burns" | "zoom" | "fade" | "parallax" | "static"
 * overlay: "dark" | "light" | "primary" | "accent" | "none"
 */
export const HeroImage = ({ src, alt = "", animation = "ken-burns", overlay = "dark", overlayOpacity = 55, className = "" }) => {
    const imgRef = useRef(null);

    useEffect(() => {
        if (animation !== "parallax") return;
        const el = imgRef.current;
        if (!el) return;
        const onScroll = () => {
            const y = window.scrollY;
            el.style.transform = `translate3d(0, ${y * 0.25}px, 0) scale(1.08)`;
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [animation]);

    const animClass = `hero-anim-${animation || "static"}`;

    return (
        <div className={`absolute inset-0 overflow-hidden ${className}`} data-testid="hero-image-wrapper">
            <img
                ref={imgRef}
                src={fileUrl(src)}
                alt={alt}
                className={`w-full h-full object-cover ${animClass}`}
                data-testid="hero-image"
                data-hero-animation={animation}
            />
            <div className="absolute inset-0" style={overlayStyle(overlay, overlayOpacity)} data-testid="hero-overlay" />
        </div>
    );
};

export default HeroImage;
